export type PasswordStrengthHint =
  | 'symbol'
  | 'more_characters'
  | 'letters'
  | 'repeating_characters'
  | 'numbers';
export type PasswordStrengthClassification = '1' | '2' | '3' | '4';
type PasswordStrengthRepetitivityScore = 0 | -1;
type PasswordStrengthComplexityScore = 0 | 1 | 2 | 3 | 4;
type PasswordStrengthLengthScore = -4 | -2 | 0 | 2 | 3 | 5;

export interface PasswordStrength {
  classification?: PasswordStrengthClassification;
  complexityScore?: PasswordStrengthComplexityScore;
  finalScore?: number;
  hints: PasswordStrengthHint[];
  lengthScore?: PasswordStrengthLengthScore;
  repetitivityScore?: PasswordStrengthRepetitivityScore;
}
export class PasswordStrengthExtractor {
  public static readonly MINIMAL_LENGTH: number = 6;

  private getLengthScore = (
    password: string,
  ): {
    lengthScore: PasswordStrengthLengthScore;
    lengthHints: PasswordStrengthHint[];
  } => {
    const length = Math.max(
      password?.length ?? 0,
      PasswordStrengthExtractor.MINIMAL_LENGTH,
    );
    const hints: PasswordStrengthHint[] = [];
    if (length < 16) {
      hints.push('more_characters');
    }
    const getLengthScore = () => {
      switch (length) {
        case 6:
          return -4;
        case 7:
        case 8:
          return -2;
        case 9:
        case 10:
          return 0;
        case 11:
        case 12:
        case 13:
          return 2;
        case 14:
        case 15:
          return 3;
        default:
          // 16 and above
          return 5;
      }
    };
    return { lengthScore: getLengthScore(), lengthHints: hints };
  };

  private getComplexityScore = (
    password: string,
  ): {
    hints: PasswordStrengthHint[];
    complexityScore: PasswordStrengthComplexityScore;
  } => {
    const checks: ((password: string) => PasswordStrengthHint | undefined)[] = [
      (password = '') => (!/[a-z]/.test(password) ? 'letters' : undefined), // password has no lowercase letters
      (password = '') => (!/[A-Z]/.test(password) ? 'letters' : undefined), // password has no uppercase letters
      (password = '') => (!/[0-9]/.test(password) ? 'numbers' : undefined), // password has no numbers
      (password = '') =>
        !/[^A-Za-z0-9]/.test(password) ? 'symbol' : undefined, // require at least one special character
    ];
    const hints = checks.map((test) => test(password)).filter((a) => a);
    const complexityScore = checks.length - hints.length;
    return {
      complexityScore: complexityScore as PasswordStrengthComplexityScore,
      hints: hints as PasswordStrengthHint[],
    };
  };

  private getRepetitivityScore = (
    password: string,
  ): {
    repetitivityScore: PasswordStrengthRepetitivityScore;
    hints: PasswordStrengthHint[];
  } => {
    const hints: PasswordStrengthHint[] = [];
    const repeatingCharExpression = /(.)\1{2,}/g;
    let repeatingCharsCount = 0;
    let repeatingChars = repeatingCharExpression.exec(password);

    while (repeatingChars) {
      const [match] = repeatingChars;
      repeatingCharsCount = Math.max(match.length, repeatingCharsCount);
      repeatingChars = repeatingCharExpression.exec(password);
    }
    const isRepetitive = repeatingCharsCount / password?.length > 0.5;
    if (isRepetitive) {
      hints.push('repeating_characters');
    }

    const { lengthScore } = this.getLengthScore(password);

    const repetitivityScore = isRepetitive && lengthScore < 3 ? -1 : 0;

    return {
      repetitivityScore,
      hints,
    };
  };

  private getScoreLabel = (score: number): PasswordStrengthClassification => {
    score = Math.max(score, 0);
    switch (score) {
      case 0:
      case 1:
        return '1'; // weak
      case 2:
        return '2'; // average
      case 3:
      case 4:
        return '3'; // good
      default:
        // 5 and above - strong
        return '4';
    }
  };

  getPasswordStrength(password: string): PasswordStrength {
    const { lengthScore, lengthHints } = this.getLengthScore(password);

    const { complexityScore, hints: complexityHints } = this.getComplexityScore(
      password,
    );

    const {
      repetitivityScore,
      hints: repetitivityHints,
    } = this.getRepetitivityScore(password);

    const finalScore = complexityScore + lengthScore + repetitivityScore;

    return {
      repetitivityScore,
      finalScore,
      classification: this.getScoreLabel(finalScore),
      complexityScore,
      lengthScore,
      hints: lengthHints.concat(complexityHints.concat(repetitivityHints)),
    };
  }

  getPasswordStrengthForBiEvent(password: string): number {
    let score = this.getPasswordStrength(password).finalScore ?? 0;
    score = Math.max(score, 0);
    switch (score) {
      case 0:
      case 1:
        return 1;
      case 2:
      case 3:
        return 3;
      default:
        return 5;
    }
  }
}
